/**
 * SIMS-9033 — what the administrator sees about a threshold change that is running, or that ran.
 *
 * The save returns before the cohort is written (AC1), so this is the only place the outcome is
 * ever reported. It has to survive navigation and time: AC3 says the result is still there "later
 * or on another day", which is why it is read from the Job rather than held in the page.
 */

export type PropagationJobStatus =
	| "IN_PROGRESS"
	| "COMPLETED"
	| "PARTIAL"
	| "FAILED";

export interface PropagationJob {
	/** Needed to retry this specific run (SIMS-9034). */
	_id?: string;
	status: PropagationJobStatus;
	startedAt: string;
	completedAt?: string;
	processed: number;
	skipped: number;
	failed: number;
	thresholdPropagation?: {
		templateName: string;
		field: "TEMPLATE_DEFAULT" | "SEGMENT";
		segmentWeekRange?: { start: number; end: number };
		previousValue: number;
		nextValue: number;
	};
}

export type BannerSeverity = "info" | "success" | "warning" | "error";

export interface PropagationBanner {
	severity: BannerSeverity;
	title: string;
	detail: string;
	/** AC5 — a run that could not reach everyone must not read as fully applied. */
	isComplete: boolean;
	/** SIMS-9034 offers a retry only when something is actually retryable. */
	canRetry: boolean;
}

/** "segment 2 (weeks 7-12)" or "the plan default" — FR-8 wants the field named, not implied. */
const fieldLabel = (job: PropagationJob): string => {
	const detail = job.thresholdPropagation;
	if (!detail) {
		return "the disenrollment threshold";
	}
	if (detail.field === "SEGMENT" && detail.segmentWeekRange) {
		return `the segment covering weeks ${detail.segmentWeekRange.start}-${detail.segmentWeekRange.end}`;
	}
	return "the plan default";
};

const valueChange = (job: PropagationJob): string => {
	const detail = job.thresholdPropagation;
	return detail
		? ` from ${detail.previousValue} to ${detail.nextValue} days`
		: "";
};

const participants = (n: number): string =>
	`${n} participant${n === 1 ? "" : "s"}`;

/**
 * The banner for the most recent propagation on a plan, or null when there has never been one.
 *
 * Deliberately says what happened to EVERY participant, not just the successes: AC3 asks for
 * updated, left alone, and could-not-be-reached, because a number that only counts successes
 * cannot be reconciled against the cohort the administrator was shown at confirmation time.
 */
export const propagationBanner = (
	job: PropagationJob | null | undefined,
): PropagationBanner | null => {
	if (!job) {
		return null;
	}

	const what = `${fieldLabel(job)}${valueChange(job)}`;

	// AC2 — in progress, and how far it has got. The counts are live: the job records them as it
	// works, so this is real progress rather than a spinner.
	if (job.status === "IN_PROGRESS") {
		return {
			severity: "info",
			title: "Applying this change to current participants…",
			detail: `Changing ${what}. ${participants(job.processed)} updated so far.`,
			isComplete: false,
			canRetry: false,
		};
	}

	if (job.status === "FAILED") {
		return {
			severity: "error",
			title: "This change could not be applied",
			detail: `Changing ${what} did not complete. No participant was left partly updated — each is either changed or untouched.`,
			isComplete: false,
			canRetry: true,
		};
	}

	// AC5 — some could not be reached, so this must NOT read as fully applied.
	if (job.status === "PARTIAL") {
		return {
			severity: "warning",
			title: "This change reached some participants, but not all",
			detail:
				`Changed ${what}. ${participants(job.processed)} updated, ` +
				`${job.skipped} left as they were, ` +
				`${job.failed} could not be reached.`,
			isComplete: false,
			canRetry: true,
		};
	}

	return {
		severity: "success",
		title: "This change has been applied to current participants",
		detail:
			`Changed ${what}. ${participants(job.processed)} updated, ` +
			`${job.skipped} left as they were.`,
		isComplete: true,
		canRetry: false,
	};
};

/**
 * Should the page keep asking? Only while work is actually outstanding.
 *
 * Polling a finished job forever would be a background request per plan view for no benefit, and
 * AC3's durable result does not need refreshing — it cannot change.
 */
export const shouldPollPropagation = (
	job: PropagationJob | null | undefined,
): boolean => job?.status === "IN_PROGRESS";
